/**
 * Optional LiDAR / RoomPlan capture helper.
 *
 * Use after the 2D room-session workflow is stable. LiDAR provides wall
 * geometry, dimensions, camera pose, and coverage — not hidden-damage detection.
 * Compact parametric geometry is returned; raw point clouds are never kept.
 */
import { Capacitor } from '@capacitor/core';
import RoomPlanPlugin from '../plugins/RoomPlanPlugin';

export async function isRoomPlanAvailable() {
  if (Capacitor.getPlatform() !== 'ios') {
    return { available: false, reason: 'not_ios' };
  }
  try {
    return await RoomPlanPlugin.isAvailable();
  } catch {
    return { available: false, reason: 'plugin_unavailable' };
  }
}

/**
 * Run a RoomPlan scan and return compact surface geometry for anchoring.
 *
 * Shape:
 *   {
 *     surfaces: [{ id, kind, width_m, height_m, transform }],
 *     dimensions: { width_m, depth_m, height_m },
 *     wall_map_2d: [{ surface_id, u0, v0, u1, v1 }],
 *     captured_at: ISO string
 *   }
 */
export async function scanRoomWithLidar() {
  const status = await isRoomPlanAvailable();
  if (!status.available) {
    const err = new Error(
      status.reason === 'not_ios'
        ? 'LiDAR RoomPlan requires an iPhone with LiDAR'
        : 'RoomPlan is not available on this device'
    );
    err.code = status.reason || 'unavailable';
    throw err;
  }
  return RoomPlanPlugin.scanRoom();
}
